import type { Node, Pie, NodesData } from '../types'
import { Event } from '../types'
import { EntityRegistry } from '../core/entity-registry'
import { EventBus } from '../core/event-bus'
import type { OverlayRenderer } from './overlay-renderer'
import { findValidPosition } from '../utils/collision-detection'
import * as PIXI from 'pixi.js'

// TODO Remove magic numbers
export class NodeRenderer {
  private readonly registry: EntityRegistry = EntityRegistry.getInstance()
  private readonly eventBus: EventBus = EventBus.getInstance()
  private overlayRenderer: OverlayRenderer
  private readonly nodeToPieMap: Map<string, string> = new Map()
  private readonly nodeOffsets: Map<string, { x: number, y: number }> = new Map()

  public initialize (overlayRenderer: OverlayRenderer): void {
    this.overlayRenderer = overlayRenderer

    this.eventBus.subscribe(Event.ADD_NODES, (data: NodesData) => {
      const sliceGraphic = this.registry.getPieSlice(data.pie.id, data.slice.id)

      if (sliceGraphic !== null) {
        this.addNodes(data)
      }
    })

    // Update node positions when pies move (from force simulation)
    this.eventBus.subscribe(Event.PIE_POSITIONS_UPDATED, () => {
      this.updateNodePositions()
    })
  }

  public addNodes (data: NodesData): void {
    const sliceGraphic = this.registry.getPieSlice(data.pie.id, data.slice.id)

    if (sliceGraphic === null) {
      return
    }

    const nodeGraphics = this.createNodeGraphics(data.nodes, sliceGraphic)

    if (nodeGraphics.length > 0) {
      this.overlayRenderer.addGraphics(nodeGraphics)
      this.positionNodesGlobally(data.pie, sliceGraphic, nodeGraphics)
      this.eventBus.publish(Event.SHOW_NODES, data)
      this.eventBus.publish(Event.REDRAW)
    }
  }

  private positionNodesGlobally (pie: Pie, sliceGraphic: PIXI.Graphics, nodeGraphics: PIXI.Graphics[]): void {
    const centerX = sliceGraphic.position.x
    const centerY = sliceGraphic.position.y
    const nodeRadius = 55
    const maxSearchRadius = 8000
    const minSeparation = 10

    for (const nodeGraphic of nodeGraphics) {
      // Get all existing graphics with bounds (pies + already placed nodes)
      const existingBounds = this.registry.getAllGraphicsWithBounds()

      // Find a valid position for this node
      const position = findValidPosition(
        centerX,
        centerY,
        nodeRadius,
        existingBounds,
        maxSearchRadius,
        minSeparation
      )

      if (position !== null) {
        nodeGraphic.position.set(position.x, position.y)
        // Store the offset from pie center for later updates
        const offsetX = position.x - centerX
        const offsetY = position.y - centerY
        this.nodeOffsets.set(nodeGraphic.name, { x: offsetX, y: offsetY })
        this.nodeToPieMap.set(nodeGraphic.name, pie.id)
      } else {
        // Fallback: place far from center if no valid position found
        const fallbackAngle = Math.random() * 2 * Math.PI
        const fallbackRadius = maxSearchRadius
        const fallbackX = centerX + fallbackRadius * Math.cos(fallbackAngle)
        const fallbackY = centerY + fallbackRadius * Math.sin(fallbackAngle)
        nodeGraphic.position.set(fallbackX, fallbackY)
        // Store the offset from pie center for later updates
        const offsetX = fallbackX - centerX
        const offsetY = fallbackY - centerY
        this.nodeOffsets.set(nodeGraphic.name, { x: offsetX, y: offsetY })
        this.nodeToPieMap.set(nodeGraphic.name, pie.id)
      }
    }
  }

  private createNodeGraphics (nodes: Node[], sliceGraphics: PIXI.Graphics): PIXI.Graphics[] {
    const graphics: PIXI.Graphics[] = []

    nodes.forEach(node => {
      const nodeGraphic = this.nodeGraphics(node, sliceGraphics)
      this.registry.registerNode(node.id, nodeGraphic)
      graphics.push(nodeGraphic)
    })

    return graphics
  }

  private nodeGraphics (node: Node, sliceGraphics: PIXI.Graphics): PIXI.Graphics {
    const graphics = new PIXI.Graphics()
    const x = sliceGraphics.position.x
    const y = sliceGraphics.position.y
    const radius = 55
    const cropRadius = radius * 0.9

    // Set a name for tracking
    graphics.name = node.id

    if (node.colors.length === 1) {
      this.renderSolidBackground(graphics, radius, node.colors[0].int)
    } else {
      this.renderSegmentedBackground(graphics, radius, node.colors.map(c => c.int))
    }

    this.renderWhiteOverlay(graphics, cropRadius)

    graphics.position.set(x, y)
    graphics.eventMode = 'static'
    graphics.cursor = 'pointer'
    graphics.scale.set(0)

    graphics.on('click', () => this.eventBus.publish(Event.NODE_CLICKED, { nodeId: node.id }))

    if (node.blobId !== undefined && node.blobId !== '') {
      void this.loadNodeImage(node.id, node.blobId, graphics, cropRadius)
    }

    return graphics
  }

  private renderSolidBackground (graphics: PIXI.Graphics, radius: number, color: number): void {
    const background = new PIXI.Graphics()
    background.beginFill(color)
    background.drawCircle(0, 0, radius)
    background.endFill()
    graphics.addChild(background)
  }

  private renderSegmentedBackground (graphics: PIXI.Graphics, radius: number, colors: number[]): void {
    const background = new PIXI.Graphics()
    const anglePerSegment = (Math.PI * 2) / colors.length
    colors.forEach((color, index) => {
      const startAngle = index * anglePerSegment
      const endAngle = (index + 1) * anglePerSegment

      background.beginFill(color)
      background.moveTo(0, 0)
      background.arc(0, 0, radius, startAngle, endAngle)
      background.lineTo(0, 0)
      background.endFill()
    })

    graphics.addChild(background)
  }

  private renderWhiteOverlay (graphics: PIXI.Graphics, radius: number): void {
    const overlay = new PIXI.Graphics()
    overlay.beginFill(0xFFFFFF)
    overlay.drawCircle(0, 0, radius)
    overlay.endFill()
    graphics.addChild(overlay)
  }

  private async loadNodeImage (nodeId: string, blobId: string, graphics: PIXI.Graphics, radius: number): Promise<void> {
    try {
      const response = await fetch(`/api/v3/blobs/${blobId}.json`)
      if (!response.ok) {
        throw new Error(`API returned ${response.status} for blob ID ${blobId}`)
      }

      const data = await response.json()
      if (data === undefined || data === null || data.url === undefined || data.url === '') {
        throw new Error(`Missing URL in API response for blob ID ${blobId}`)
      }

      const imageResponse = await fetch(data.url)
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.status} ${imageResponse.statusText}`)
      }

      const contentType = imageResponse.headers.get('content-type')
      if (contentType === null || contentType === undefined || !contentType.includes('image/')) {
        throw new Error(`Response is not an image: ${contentType ?? 'unknown content type'}`)
      }

      const blob = await imageResponse.blob()
      if (blob.size === 0) {
        throw new Error('Received empty blob')
      }

      const url = URL.createObjectURL(blob)
      const texture = PIXI.Texture.from(url)
      const sprite = new PIXI.Sprite(texture)

      const imageSize = radius * 2
      sprite.width = imageSize
      sprite.height = imageSize
      sprite.anchor.set(0.5)

      const existingSprite = graphics.children.find(child => child instanceof PIXI.Sprite)
      if (existingSprite !== undefined) {
        graphics.removeChild(existingSprite)
      }

      graphics.addChild(sprite)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error(`Failed to load image for node ${nodeId}:`, error)
    }
  }

  private updateNodePositions (): void {
    this.registry.getAllNodes().forEach(nodeGraphic => {
      const pieId = this.nodeToPieMap.get(nodeGraphic.name)
      const offset = this.nodeOffsets.get(nodeGraphic.name)

      if (pieId != null && offset != null) {
        const pieMetadata = this.registry.getPieMetadata(pieId)

        if (pieMetadata != null) {
          nodeGraphic.position.set(pieMetadata.centerX + offset.x, pieMetadata.centerY + offset.y)
        }
      }
    })
  }
}
